const API_BASE_URL = '/api';

export interface EvaluationResult {
  score: number;
  feedback: string;
  evaluatedAt: string;
}

export interface EvaluationStats {
  questionId: string;
  questionTitle: string;
  totalResponses: number;
  evaluatedResponses: number;
  unevaluatedResponses: number;
  averageScore: number;
  scoreDistribution: {
    excellent: number; // 90-100
    good: number;      // 70-89
    average: number;   // 50-69
    poor: number;      // 0-49
  };
  evaluationRate: number;
}

export interface BatchEvaluationResult {
  success: boolean;
  message: string;
  evaluatedCount: number;
  errorCount: number;
  results: Array<{
    responseId: string;
    studentName: string;
    score?: number;
    error?: string;
    success: boolean;
  }>;
}

class EvaluationApiService {
  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erreur inconnue' }));
      throw new Error(errorData.error || `Erreur HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Évalue une réponse spécifique à une question ouverte
   */
  async evaluateResponse(questionId: string, responseId: string): Promise<EvaluationResult> {
    const response = await this.makeRequest<{ success: boolean; evaluation: EvaluationResult }>(
      `/evaluation/evaluate-open-question/${questionId}/${responseId}`,
      { method: 'POST' }
    );

    if (!response.success) {
      throw new Error('Échec de l\'évaluation');
    }

    return response.evaluation;
  }

  /**
   * Évalue toutes les réponses non évaluées d'une question ouverte
   */
  async evaluateAllResponses(questionId: string): Promise<BatchEvaluationResult> {
    return this.makeRequest<BatchEvaluationResult>(
      `/evaluation/evaluate-all-responses/${questionId}`,
      { method: 'POST' }
    );
  }

  /**
   * Obtient les statistiques d'évaluation d'une question ouverte
   */
  async getEvaluationStats(questionId: string): Promise<EvaluationStats> {
    return this.makeRequest<EvaluationStats>(`/evaluation/stats/${questionId}`);
  }

  /**
   * Vérifie le statut de configuration du service d'évaluation
   */
  async getEvaluationStatus(): Promise<{ configured: boolean; message: string }> {
    return this.makeRequest<{ configured: boolean; message: string }>('/evaluation/status');
  }
}

export const evaluationApi = new EvaluationApiService();
